import { ArrowRight, CheckCircle2, Clock3, Lock, PackageSearch, AlertTriangle, ShieldAlert, FileWarning, Upload, CircleDot, Paperclip, ChevronRight } from "lucide-react";
import { Badge, Mono, toneBox, type Tone } from "@/components/ui";
import { BLOKE_LABEL, DNV, HIDRO, MY_QUEUE, RISERS, STATIONS, STATION_BATCH, type BlokeKodu } from "@/lib/mock";
import { cn } from "@/lib/cn";
import { cevir } from "@/lib/dil";

const toplam = STATIONS.map((_, i) => Object.values(STATION_BATCH).reduce((a, r) => a + (r[i] ?? 0), 0));
const total = toplam.reduce((a, b) => a + b, 0);

const blokeTone: Record<BlokeKodu, Tone> = {
  "DISPOSITION-BEKLIYOR": "warn", "YEDEK-PARCA": "bad", "FOUND-CONDITION": "bad", "DOKUMAN-EKSIK": "warn",
  "SERTIFIKA/KALIBRASYON": "warn", "SET-BEKLIYOR": "bad", "KIMLIK-DOGRULAMA": "hold",
};
const blokeIcon: Record<BlokeKodu, React.ElementType> = {
  "DISPOSITION-BEKLIYOR": Clock3, "YEDEK-PARCA": PackageSearch, "FOUND-CONDITION": AlertTriangle, "DOKUMAN-EKSIK": FileWarning,
  "SERTIFIKA/KALIBRASYON": ShieldAlert, "SET-BEKLIYOR": Lock, "KIMLIK-DOGRULAMA": ShieldAlert,
};

/** Akış nehri: sıralı tek renk (lacivert) kademeleri; SERT ayrı. */
const nehirRenk = (i: number) => `color-mix(in oklab, var(--brand-navy) ${28 + i * 6}%, var(--surface))`;

export function HomeB() {
  const bloke = RISERS.filter((r) => r.bloke);
  const hold = RISERS.flatMap((r) => r.hatlar.filter((h) => h.durum === "hold").map((h) => ({ rn: r.rn, hat: h.hat, ist: h.istasyon })));

  return (
    <div className="space-y-6">
      {/* Başlık */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight sm:text-3xl">{cevir("Today, 24 August")}</h1>
          <p className="mt-1 text-base text-ink-2">{cevir("85 active risers · 425 components · B1–B4 · cycle 2024–29")}</p>
        </div>
        <div className="flex gap-2">
          <button className="rounded-full border border-line bg-surface px-4 py-2 text-base font-semibold hover:border-line-strong">{cevir("Weekly report")}</button>
          <button className="rounded-full bg-orange px-4 py-2 text-base font-semibold text-white hover:opacity-90">{cevir("+ New record")}</button>
        </div>
      </div>

      {/* Üç büyük sayı */}
      <div className="grid gap-3 sm:grid-cols-3">
        <Hero baslik={cevir("Delivered")} deger="43" alt="/ 85 riser" not={cevir("3 COC received · 40 DNV in review")} tone="ok" />
        <Hero baslik={cevir("Flow blocker")} deger={String(bloke.length)} alt="riser" not={cevir("4 spare parts · 2 Disposition · 1 ID")} tone="bad" />
        <Hero baslik={cevir("Next in line today")} deger={String(MY_QUEUE.length)} alt={cevir("tasks")} not={cevir("2 urgent: fit-up approval, disposition verification")} tone="orange" />
      </div>

      {/* Akış nehri */}
      <section className="rounded-2xl border border-line bg-surface p-4 shadow-[var(--shadow)] sm:p-5">
        <div className="flex flex-col gap-0.5 sm:flex-row sm:items-baseline sm:justify-between">
          <h2 className="text-md font-bold">{cevir("Component flow")}</h2>
          <span className="text-sm text-ink-3">{cevir("Distribution of 425 components to stations · click → station queue")}</span>
        </div>
        <div className="mt-3 flex h-9 w-full gap-[2px] overflow-hidden rounded-lg">
          {toplam.map((n, i) => n > 0 ? (
            <button
              key={i}
              title={`${STATIONS[i]!.ad}: ${n}`}
              className="group relative min-w-[6px] transition hover:brightness-110"
              style={{ flex: n, background: STATIONS[i]!.code === "SERT" ? "var(--brand-orange)" : nehirRenk(i) }}
            >
              {n / total > 0.06 && <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-white tnum">{n}</span>}
            </button>
          ) : null)}
        </div>
        <div className="scroll-none mt-2 flex gap-3 overflow-x-auto text-xs text-ink-3">
          {toplam.map((n, i) => n > 0 ? (
            <span key={i} className="inline-flex shrink-0 items-center gap-1">
              <i className="h-2 w-2 rounded-sm" style={{ background: STATIONS[i]!.code === "SERT" ? "var(--brand-orange)" : nehirRenk(i) }} />
              {STATIONS[i]!.kisa} <span className="tnum">{n}</span>
            </span>
          ) : null)}
        </div>
        <div className="mt-4 grid gap-3 text-sm sm:grid-cols-3">
          <Mini baslik={cevir("Hydrotest")} deger={`${HIDRO.done} / ${HIDRO.done + HIDRO.pending}`} alt={cevir`${HIDRO.red} RED · 6 in 20–80% scale`} />
          <Mini baslik="DNV VerCom" deger={`${DNV.acik} open`} alt={cevir`${DNV.toplam - DNV.acik} of ${DNV.toplam} comments closed · Rev 14`} />
          <Mini baslik="Disposition SLA" deger={cevir("19 days")} alt={cevir("oldest: R117 Kill · threshold 14 d (temporary)")} warn />
        </div>
      </section>

      {/* Şeritler */}
      <div className="grid gap-4 lg:grid-cols-3">
        <Lane baslik={cevir("Flow blockers")} alt={cevir`${bloke.length} riser · block code mandatory`} tone="bad">
          {bloke.map((r) => {
            const Icon = blokeIcon[r.bloke!];
            return (
              <li key={r.rn} className="rounded-xl border border-line bg-surface p-3 transition hover:border-line-strong">
                <div className="flex items-center gap-2">
                  <span className={cn("flex h-7 w-7 items-center justify-center rounded-lg", toneBox[blokeTone[r.bloke!]])}><Icon size={15} /></span>
                  <Mono className="font-bold text-ink">{r.rn}</Mono>
                  <span className="text-sm text-ink-3">{r.darbogaz !== "—" ? r.darbogaz + " line" : ""}</span>
                  <span className="ml-auto text-xs text-ink-3 tnum">{r.gun} {cevir("d")}</span>
                </div>
                <div className="mt-2 text-sm text-ink-2">{r.bekleyen}</div>
                <div className="mt-2 flex items-center justify-between">
                  <Badge tone={blokeTone[r.bloke!]}>{BLOKE_LABEL[r.bloke!]}</Badge>
                  <button className="inline-flex items-center gap-1 text-sm font-semibold text-orange">{cevir("Open")} <ArrowRight size={12} /></button>
                </div>
              </li>
            );
          })}
        </Lane>

        <Lane baslik={cevir("Waiting at gate")} alt={cevir("G1 hold point · signature / release")} tone="hold">
          {hold.map((h) => (
            <li key={h.rn + h.hat} className="rounded-xl border border-line bg-surface p-3">
              <div className="flex items-center gap-2">
                <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-hold-bg text-hold"><CheckCircle2 size={15} /></span>
                <Mono className="font-bold text-ink">{h.rn}</Mono><span className="text-sm text-ink-2">{h.hat} {cevir("line ·")} {STATIONS.find((s) => s.code === h.ist)?.ad}</span>
              </div>
              <div className="mt-2 text-sm text-ink-2">{cevir("Fit-up preparation complete, waiting for quality approval and release. Foreman: Mutlu T.")}</div>
              <div className="mt-2 flex items-center justify-between">
                <Badge tone="hold">G1 · Duvar 11</Badge>
                <button className="rounded-full bg-navy px-3 py-1 text-sm font-semibold text-white dark:text-[#0a141c]">{cevir("Go to approval")}</button>
              </div>
            </li>
          ))}
          <li className="rounded-xl border border-dashed border-line p-3 text-sm text-ink-3">
            {cevir("R157 Disassembly closure: closes when signed FM-0011 scan is uploaded (print-sign-upload).")}
          </li>
        </Lane>

        <Lane baslik={cevir("My work")} alt={cevir("role: PM · super user")} tone="orange">
          {MY_QUEUE.map((q) => (
            <li key={q.id} className="flex items-start gap-3 rounded-xl border border-line bg-surface p-3">
              <span className={cn("mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg", q.tip === "onay" ? "bg-hold-bg text-hold" : q.tip === "yukle" ? "bg-info-bg text-info" : q.tip === "karar" ? "bg-warn-bg text-warn" : "bg-surface-2 text-ink-2")}>
                {q.tip === "onay" ? <CheckCircle2 size={14} /> : q.tip === "yukle" ? <Upload size={14} /> : q.tip === "karar" ? <CircleDot size={14} /> : <Paperclip size={14} />}
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-1.5 text-base font-semibold"><span className="truncate">{q.baslik}</span>{q.acil && <span className="h-1.5 w-1.5 rounded-full bg-orange" />}</span>
                <span className="mt-0.5 block truncate text-xs text-ink-3">{q.alt}</span>
              </span>
              <ChevronRight size={16} className="mt-1 text-ink-3" />
            </li>
          ))}
        </Lane>
      </div>
    </div>
  );
}

function Hero({ baslik, deger, alt, not, tone }: { baslik: string; deger: string; alt: string; not: string; tone: "ok" | "bad" | "orange" }) {
  const c = { ok: "text-ok", bad: "text-bad", orange: "text-orange" }[tone];
  return (
    <div className="rounded-2xl border border-line bg-surface p-5 shadow-[var(--shadow)]">
      <div className="text-sm font-semibold uppercase tracking-wider text-ink-3">{baslik}</div>
      <div className="mt-2 flex items-baseline gap-2">
        <span className={cn("text-[44px] font-extrabold leading-none tracking-tight tnum", c)}>{deger}</span>
        <span className="text-base text-ink-2">{alt}</span>
      </div>
      <div className="mt-2 text-sm text-ink-2">{not}</div>
    </div>
  );
}

function Mini({ baslik, deger, alt, warn }: { baslik: string; deger: string; alt: string; warn?: boolean }) {
  return (
    <div className="rounded-xl bg-surface-2 px-3 py-2.5">
      <div className="text-xs font-semibold text-ink-3">{baslik}</div>
      <div className={cn("text-md font-bold tnum", warn && "text-warn")}>{deger}</div>
      <div className="text-xs text-ink-2">{alt}</div>
    </div>
  );
}

function Lane({ baslik, alt, tone, children }: { baslik: string; alt: string; tone: "bad" | "hold" | "orange"; children: React.ReactNode }) {
  const bar = { bad: "bg-bad", hold: "bg-hold", orange: "bg-orange" }[tone];
  return (
    <section className="rounded-2xl bg-surface-2/70 p-3">
      <header className="flex items-center gap-2 px-1 pb-3">
        <span className={cn("h-5 w-1 rounded-full", bar)} />
        <h2 className="text-base font-bold">{baslik}</h2>
        <span className="text-xs text-ink-3">{alt}</span>
      </header>
      <ul className="space-y-2">{children}</ul>
    </section>
  );
}
